<template>
  <div class="flex items-center justify-center rounded-[30px] p-6 w-full md:w-[455px] bg-white shadow-[0px_1px_3px_0px_rgba(13,13,13,0.1)] m-auto">
    <div class="w-full space-y-8">
      <div v-if="settings.show_logo" class="flex justify-center">
        <img 
          :src="settings.siteLogo || '/wp-includes/images/w-logo-blue.png'" 
          alt="Site Logo" 
          class="h-16 w-auto mb-4"
        />
      </div>

      <!-- Reset Password Form -->
      <div v-if="showResetForm">
        <reset-password-form 
          :reset-key="resetKey"
          :reset-login="resetLogin"
        />
      </div>

      <!-- Phone Input Form -->
      <form v-else-if="!isOtpMode && !isEmailMode && !showUserInfoForm && !showCustomFields" 
            class="mt-8 space-y-4" 
            @submit.prevent="handleSubmit">
        <div>
          <span class="text-[#5B5C60] text-[14px] font-normal mb-2">{{ t('phoneNumber') }}</span>
          <div class="relative">
  
            <!-- Desktop Input (hidden on mobile) -->
            <input
              v-if="!isMobile"
              v-model="phoneWithoutPrefix"
              type="text"
              inputmode="text"
              @input="onPhoneInput"
              @keydown="onPhoneKeyDown"
              :placeholder="t('phoneOrEmail')"
              class="w-full h-14 pl-3 pr-3 !border !border-[#e6e6e6] !rounded-3xl text-left ltr"
            />
            <!-- Mobile Input (hidden on desktop) -->
            <input
              v-else
              v-model="phoneWithoutPrefix"
              type="number"
              inputmode="numeric"
              pattern="[0-9]*"
              @input="onPhoneInput"
              @keydown="onPhoneKeyDown"
              :placeholder="t('phoneOrEmail')"
              class="w-full h-14 pl-3 pr-3 !border !border-[#e6e6e6] !rounded-3xl text-left ltr"
            />
          </div>
        </div>
        <button 
          type="submit"
          class="w-full flex justify-center items-center py-3 px-6 border border-transparent text-base font-medium rounded-full text-[#0D0D0D] bg-main  focus:outline-none focus:ring-0 !mt-8"
          :disabled="loading || !isValidPhone"
        >
          <span v-if="loading" class="spinner-loading"></span>
          <span v-else>{{ t('sendOtp') }}</span>
        </button>
        <button
          type="button"
          @click="isEmailMode = true"
          class="text-base rounded-full text-[#0D0D0D] bg-[#F2F2F2] text-center w-full mt-0 focus:outline-none ring-0 py-3 px-6"
        >
          {{ t('loginWithPassword') }}
        </button>
       
    
      </form>

      <!-- Other forms -->
      <div v-else-if="isOtpMode" class="space-y-4">
        <otp-input
          ref="otpInputRef"
          v-model="credentials.otp"
          :resend-delay="settings.settings?.resend_delay || 60"
          :otp-length="settings.settings?.otp_length || 4"
          @resend="handleResendOtp"
          @complete="verifyOtp"
        />
        
          <button
            @click="verifyOtp"
            :disabled="loading"
            class="w-full flex justify-center items-center py-3 px-6 border border-transparent text-base font-medium rounded-full text-[#0D0D0D] bg-main  focus:outline-none focus:ring-0"
          >
            <span v-if="loading" class="spinner-loading"></span>
            <span v-else>{{ $t('verify') }}</span>
          </button>
          <button
            @click="handleBack"
            class="w-full flex justify-center items-center py-3 px-6 text-base font-medium text-[#0D0D0D]"
          >
            {{ $t('back') }}
          </button>
       
   
      </div>

      <div v-else-if="isEmailMode">
        <form class="mt-8 space-y-4" @submit.prevent="handleEmailLogin">
          <div class="rounded-md -space-y-px">
            <div class="mb-4">
              <input
                type="email"
                v-model="credentials.email"
                required
                class="appearance-none rounded-[32px] relative block w-full px-3 py-2 h-14 border border-[#e6e6e6] placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                :placeholder="t('email')"
              />
            </div>
            <div>
              <input
                type="password"
                v-model="credentials.password"
                required
                class="appearance-none rounded-[32px] relative block w-full px-3 py-2 h-14 border border-[#e6e6e6] placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                :placeholder="t('password')"
              />
            </div>
          </div>

          <div class="flex items-center justify-between">
            <button
              type="button"
              @click="showForgotPassword = true"
              class="hover:text-main text-sm focus:outline-none ring-0"
            >
              {{ t('forgotPassword') }}
            </button>
          </div>

        
         
          <button
            type="submit"
            class="w-full flex justify-center items-center py-3 px-6 border border-transparent text-base font-medium rounded-full text-[#0D0D0D] bg-main  focus:outline-none focus:ring-0 !mt-8"
            :disabled="loading"
          >
            <span v-if="loading" class="spinner-loading"></span>
            <span v-else>{{ t('login') }}</span>
          </button>
          <button
              type="button"
              @click="isEmailMode = false"
              class="text-base rounded-full text-[#0D0D0D] bg-[#F2F2F2] text-center w-full mt-0 focus:outline-none ring-0 py-3 px-6"
            >
              {{ t('loginWithOtpCode') }}
            </button>
      
        </form>
      </div>

      <div v-else-if="showUserInfoForm">
        <user-info-form
          :loading="loading"
          :require-email="settings.settings?.require_email"
          :require-first-name="settings.settings?.require_first_name"
          :require-last-name="settings.settings?.require_last_name"
          :custom-fields="settings.settings?.custom_fields || []"
          :phone="credentials.username"
          @submit="handleUserInfoSubmit"
        />
      </div>

      <div v-else-if="showCustomFields">
        <custom-fields-form
          :custom-fields="settings.settings?.custom_fields || []"
          :loading="loading"
          @submit="handleCustomFields"
          @skip="handleSkipCustomFields"
        />
      </div>

      <div v-if="showForgotPassword" class="fixed inset-0 !m-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
        <div class="bg-white p-8 rounded-lg max-w-md w-full mx-4">
          <h3 class="text-lg font-medium mb-4">{{ t('resetPassword') }}</h3>
          
          <form @submit.prevent="handleForgotPassword" class="space-y-4">
            <div>
              <input
                type="email"
                v-model="forgotPasswordEmail"
                required
                class="appearance-none rounded-[32px] relative block w-full px-3 py-2 h-14 border border-[#e6e6e6] placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                :placeholder="t('email')"
              />
            </div>

            <div class="flex gap-4">
              <button
                type="button"
                @click="showForgotPassword = false"
                class="flex-1 flex justify-center items-center py-2 px-4 border border-main text-sm font-medium rounded-[32px] h-14 text-black bg-white focus:outline-none focus:ring-0"
              >
                {{ t('cancel') }}
              </button>
              
              <button
                type="submit"
                class="flex-1 flex justify-center items-center py-2 px-4 border border-transparent text-sm font-medium rounded-[32px] h-14 text-white bg-main hover:contrast-150 focus:outline-none focus:ring-0"
                :disabled="loading"
              >
                <span v-if="loading" class="spinner-loading"></span>
                <span v-else>{{ t('sendResetLink') }}</span>
              </button>
            </div>
          </form>
        </div>
      </div>

      <span v-if="settings.show_copyright" class="text-[#5B5C60] text-[14px] font-normal justify-center flex pt-1 text-left gap-1" style="direction: ltr;">
       <span>🔐</span> Verified by <a target="_blank" rel="nofollow" href="https://sms.ir/">SMS.ir</a> | <a :href="smsir.siteUrlFull">{{ smsir.siteUrl }}</a>
      </span>
      
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, onUnmounted } from 'vue'
import 'vue3-tel-input/dist/vue3-tel-input.css'
import OtpInput from './OtpInput.vue'
import { showSuccess, showError } from '../../utils/swal'
import CustomFieldsForm from './CustomFieldsForm.vue'
import { t } from '../../utils/translate'
import EmailForm from './EmailForm.vue'
import UserInfoForm from './UserInfoForm.vue'
import ResetPasswordForm from './ResetPasswordForm.vue'
import { convertToEnglishNumbers, isPersianOrArabicNumber } from '../../utils/number'

const smsir = window.sms_ir_login || {}
const $ = window.jQuery

const settings = ref({})

const fetchSettings = async () => {
  try {
    const data = {
      action: 'smsir_get_settings',
      nonce: smsir.nonce
    }

    const response = await $.post(smsir.ajaxUrl, data)
    if (response.success) {
      settings.value = response.data
    } else {
      throw new Error(response.data?.message || 'Failed to load settings')
    }
  } catch (err) {
    console.error('Failed to fetch settings:', err)
    showError(t('generalError'))
  }
}

const isOtpMode = ref(false)
const isEmailMode = ref(false)
const loading = ref(false)
const isValidPhone = ref(false)
const phoneNumber = ref('')

const credentials = ref({
  username: '',
  password: '',
  otp: ''
})

const validatePhone = (valid, number) => {
  isValidPhone.value = valid
}

const urlParams = new URLSearchParams(window.location.search)
const action = urlParams.get('action')
const key = urlParams.get('key')
const login = urlParams.get('login')

const showResetForm = ref(action === 'rp' && key && login)
const resetKey = ref(key)
const resetLogin = ref(login)


const handleSubmit = async () => {
  try {
    loading.value = true
    
    if (isOtpMode.value) {
      await verifyOtp()
    } else if (!isEmailMode.value) {
      await requestOtp()
    } else if (isEmailMode.value) {
      await loginWithEmail()
    }
  } catch (err) {
    showError(err.message)
  } finally {
    loading.value = false
  }
}

const requestOtp = async () => {
  try {
    
    if (!phoneNumber.value) {
      showError(t('pleaseEnterValidPhone'))
      return
    }

    if (!isValidPhone.value) {
      showError(t('pleaseEnterValidPhone'))
      return
    }

    loading.value = true

    credentials.value.username = phoneNumber.value

    const data = {
      action: 'smsir_request_otp',
      phone: phoneNumber.value,
      nonce: smsir.nonce
    };

    const response = await $.post(smsir.ajaxUrl, data);

    if (response.success) {
      isOtpMode.value = true;
      showSuccess(response.data.message || t('otpSent'))
    } else {
      showError(response.data?.message || t('generalError'));
      return;
    }
  } catch (err) {
    showError(err.message || t('generalError'));
    return false;
  } finally {
    loading.value = false
  }
}

const verifyOtp = async () => {
  try {
    // Prevent multiple submissions
    if (loading.value) return
    
    loading.value = true
    
    const data = {
      action: 'smsir_verify_otp',
      phone: phoneNumber.value,
      otp: credentials.value.otp,
      nonce: smsir.nonce,
      is_modal: props.isModal ? 'true' : 'false'
    }

    const response = await $.post(smsir.ajaxUrl, data)

    if (response.success) {
      isOtpMode.value = false
      
      // If user exists, just log them in and return immediately
      if (response.data.user_exists) {
        handleLoginSuccess(response)
        return
      }

      // Only for new users: check if we need additional info
      if (response.data.needs_user_info) {
        showUserInfoForm.value = true
        showCustomFields.value = false
        showEmailForm.value = false
      } else if (response.data.needs_custom_fields) {
        showCustomFields.value = true
        showUserInfoForm.value = false
        showEmailForm.value = false
      } else {
        handleLoginSuccess(response)
      }
    } else {
      throw new Error(response.data?.message || t('invalidOtp'))
    }
  } catch (err) {
    showError(err.message || t('generalError'))
  } finally {
    loading.value = false
  }
}

const loginWithEmail = async () => {
  try {
    const data = {
      action: 'smsir_login_email',
      email: credentials.value.username,
      password: credentials.value.password,
      nonce: smsir.nonce,
      is_modal: props.isModal ? 'true' : 'false'
    };

    const response = await $.post(smsir.ajaxUrl, data);

    if (response.success) {
      showSuccess(response.data.message || t('loginSuccess'))
      if (props.isModal) {
        emit('login-success')
      }
      setTimeout(() => {
        window.location.href = response.data.redirect_url;
      }, 1500)
    } else {
      throw new Error(response.data?.message || t('invalidCredentials'));
    }
  } catch (err) {
    showError(err.message || t('generalError'));
  }
}

const showForgotPassword = ref(false)
const forgotPasswordEmail = ref('')

const handleForgotPassword = async (e) => {
  e.preventDefault()
  
  try {
    loading.value = true
    const data = {
      action: 'smsir_forgot_password',
      email: forgotPasswordEmail.value,
      nonce: smsir.nonce
    }

    const response = await $.post(smsir.ajaxUrl, data)

    if (response.success) {
      showSuccess(response.data.message || t('resetLinkSent'))
      showForgotPassword.value = false
      forgotPasswordEmail.value = ''
    } else {
      throw new Error(response.data?.message || t('emailNotFound'))
    }
  } catch (err) {
    showError(err.message || t('generalError'))
  } finally {
    loading.value = false
  }
}

const resetPassword = ref('')
const resetPasswordConfirm = ref('')
const isResetMode = ref(false)

onMounted(async () => {
  await fetchSettings()
  
  if (smsir.isResetMode && smsir.resetKey && smsir.resetLogin) {
    isResetMode.value = true;
    resetKey.value = smsir.resetKey;
    resetLogin.value = smsir.resetLogin;
  }

  checkDeviceType()

  window.addEventListener('resize', checkDeviceType)
})

const handleResetPassword = async () => {
  try {
    if (resetPassword.value !== resetPasswordConfirm.value) {
      showError(t('passwordsDoNotMatch'))
      return
    }

    loading.value = true

    const data = {
      action: 'smsir_reset_password',
      key: resetKey.value,
      login: resetLogin.value,
      password: resetPassword.value,
      nonce: smsir.nonce
    }

    const response = await $.post(smsir.ajaxUrl, data)

    if (response.success) {
      showSuccess(response.data.message)
      setTimeout(() => {
        window.location.href = response.data.redirect_url
      }, 1500)
    } else {
      throw new Error(response.data?.message || t('generalError'))
    }
  } catch (err) {
    showError(err.message || t('generalError'))
  } finally {
    loading.value = false
  }
}

const otpInputRef = ref(null)

const countdown = ref(settings.settings?.resend_delay || 60)
let timer = null

const startCountdown = () => {
  countdown.value = settings.settings?.resend_delay || 60
  clearInterval(timer)
  timer = setInterval(() => {
    if (countdown.value > 0) {
      countdown.value--
    } else {
      clearInterval(timer)
    }
  }, 1000)
}

const handleResendOtp = async () => {
  try {
    loading.value = true
    const data = {
      action: 'smsir_request_otp',
      phone: phoneNumber.value,
      nonce: smsir.nonce
    }

    const response = await $.post(smsir.ajaxUrl, data)

    if (response.success) {
      showSuccess(response.data.message || t('otpResent'))
      if (otpInputRef.value) {
        otpInputRef.value.startCountdown()
      }
    } else {
      throw new Error(response.data?.message || t('generalError'))
    }
  } catch (err) {
    showError(err.message || t('generalError'))
  } finally {
    loading.value = false
  }
}

watch(isOtpMode, (newValue) => {
  if (newValue) {
    startCountdown()
  } else {
    clearInterval(timer)
  }
})

onUnmounted(() => {
  clearInterval(timer)
  window.removeEventListener('resize', checkDeviceType)
})

const handleBack = () => {
  isOtpMode.value = false
  credentials.value.otp = ''
  clearInterval(timer)
  countdown.value = 60
}

const props = defineProps({
  isModal: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['login-success'])

const showCustomFields = ref(false)
const customFieldsData = ref({})

const handleCustomFields = async (data) => {
  try {
    loading.value = true
    const submitData = {
      action: 'smsir_save_custom_fields',
      phone: credentials.value.username,
      custom_fields: data,
      nonce: smsir.nonce,
      is_modal: props.isModal ? 'true' : 'false'
    }

    const response = await $.post(smsir.ajaxUrl, submitData)

    if (response.success) {
      showSuccess(response.data.message || t('loginSuccess'))
      if (props.isModal) {
        emit('login-success')
      }
      setTimeout(() => {
        window.location.href = response.data.redirect_url || '/'
      }, 1500)
    } else {
      throw new Error(response.data?.message || t('generalError'))
    }
  } catch (err) {
    showError(err.message || t('generalError'))
  } finally {
    loading.value = false
  }
}

const handleSkipCustomFields = async () => {
  try {
    const data = {
      action: 'smsir_complete_login',
      phone: credentials.value.username,
      nonce: smsir.nonce,
      is_modal: props.isModal ? 'true' : 'false'
    }

    const response = await $.post(smsir.ajaxUrl, data)

    if (response.success) {
      handleLoginSuccess(response)
    } else {
      throw new Error(response.data?.message || t('generalError'))
    }
  } catch (err) {
    showError(err.message || t('generalError'))
  }
}

const handleEmailLogin = async () => {
  try {
    loading.value = true
    const data = {
      action: 'smsir_login_email',
      email: credentials.value.email,
      password: credentials.value.password,
      nonce: smsir.nonce,
      is_modal: props.isModal ? 'true' : 'false'
    }

    const response = await $.post(smsir.ajaxUrl, data)

    if (response.success) {
      handleLoginSuccess(response)
    } else {
      throw new Error(response.data?.message || t('invalidCredentials'))
    }
  } catch (err) {
    showError(err.message || t('generalError'))
  } finally {
    loading.value = false
  }
}

const showEmailForm = ref(false)


const showUserInfoForm = ref(false)

const handleUserInfoSubmit = async (data) => {
  try {
    loading.value = true
    const submitData = {
      action: 'smsir_save_custom_fields',
      phone: credentials.value.username,
      email: data.email,
      first_name: data.first_name,
      last_name: data.last_name,
      custom_fields: data.custom_fields || {},
      nonce: smsir.nonce,
      is_modal: props.isModal ? 'true' : 'false'
    }

    const response = await $.post(smsir.ajaxUrl, submitData)

    if (response.success) {
      showSuccess(response.data.message || t('loginSuccess'))
      if (props.isModal) {
        emit('login-success')
      }
      setTimeout(() => {
        window.location.href = response.data.redirect_url || '/'
      }, 1500)
    } else {
      throw new Error(response.data?.message || t('generalError'))
    }
  } catch (err) {
    showError(err.message || t('generalError'))
  } finally {
    loading.value = false
  }
}

const handleLoginSuccess = async (response) => {
  try {
    showSuccess(response.data.message || t('loginSuccess'))
    
    if (props.isModal) {
      emit('login-success')
    }
    
    // Redirect after successful login
    setTimeout(() => {
      window.location.href = response.data.redirect_url || '/'
    }, 1500)
  } catch (err) {
    showError(err.message || t('generalError'))
  }
}

const hasCustomFields = computed(() => {
  return settings.value?.settings?.custom_fields?.length > 0
})

const onKeyDown = (event) => {
  // Allow: backspace, delete, tab, escape, enter, arrows
  if ([46, 8, 9, 27, 13, 110, 190].indexOf(event.keyCode) !== -1 ||
    // Allow: Ctrl+A, Command+A
    (event.keyCode === 65 && (event.ctrlKey === true || event.metaKey === true)) ||
    // Allow: home, end, left, right, down, up
    (event.keyCode >= 35 && event.keyCode <= 40)) {
    return;
  }
  
  // Ensure that it is a number and stop the keypress
  if ((event.shiftKey || (event.keyCode < 48 || event.keyCode > 57)) && (event.keyCode < 96 || event.keyCode > 105)) {
    event.preventDefault();
  }
}

const phoneWithoutPrefix = ref('')
watch(phoneWithoutPrefix, (newValue) => {
  if (newValue) {
    // For validation, ensure it starts with 9 after removing any leading 0
    const validationValue = newValue.startsWith('0') ? newValue.substring(1) : newValue
    
    // Update the full phone number with prefix
    phoneNumber.value = `${newValue}`
    
    // Validate phone number (must be 10 digits for Iranian mobile starting with 9)
    isValidPhone.value = newValue.length === 11 && newValue.startsWith('9') || 
                         (newValue.length === 10 && newValue.startsWith('9'))
  } else {
    phoneNumber.value = ''
    isValidPhone.value = false
  }
}, { immediate: true })

const onPhoneInput = (event) => {
  let value = event.target.value
  
  // Convert Persian/Arabic numbers to English
  value = convertToEnglishNumbers(value)
  
  // Keep only digits
  value = value.replace(/\D/g, '')
  
  // Handle the first digit - must be 9
  if (value.length > 0) {
    // First character must be 9
    if (value[0] !== '9') {
      // If first digit is wrong - replace value with either:
      // 1. Empty string if no valid prefix exists
      // 2. Previous valid value starting with 9
      if (phoneWithoutPrefix.value && phoneWithoutPrefix.value.startsWith('9')) {
        // Keep the previous valid value
        event.preventDefault()
        return
      } else {
        // If the input was 0, try to interpret as starting with 09
        if (value.startsWith('0') && value.length > 1) {
          // Remove the leading 0 and check if the next digit is 9
          value = value.substring(1)
          if (!value.startsWith('9')) {
            // If still doesn't start with 9, prevent
            phoneWithoutPrefix.value = ''
            showError(t('phoneStartWith9'))
            return
          }
        } else {
          // For any other first digit, prevent and show error
          phoneWithoutPrefix.value = ''
          showError(t('phoneStartWith9'))
          return
        }
      }
    }
  }
  
  // Enforce max length
  if (value.length > 10) {
    value = value.slice(0, 10)
  }
  
  // Update internal value
  phoneWithoutPrefix.value = value
}

// Add keydown event handler to prevent non-9 first digit
const onPhoneKeyDown = (event) => {
  const input = event.target
  const value = input.value
  
  // If input is empty and key pressed is not 9 or 0 (for 09...)
  if (value.length === 0 && !/[90]/.test(event.key) && /^\d$/.test(event.key)) {
    event.preventDefault()
    showError(t('phoneStartWith9'))
  }
}

// Add device detection logic
const deviceDetector = ref(null)
const isMobile = ref(false)

const checkDeviceType = () => {
  // Use a more reliable approach to detect mobile devices
  if (typeof window !== 'undefined') {
    // Use window.innerWidth as the primary detection method
    isMobile.value = window.innerWidth < 768
    
    // Additional check for mobile user agents as a fallback
    if (!isMobile.value) {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      isMobile.value = /android|webos|iphone|ipad|ipod|blackberry|IEMobile|Opera Mini/i.test(userAgent);
    }
  } else {
    // Default to desktop if window is not available
    isMobile.value = false;
  }
  }

// Force initial value to false for desktop
isMobile.value = false;
</script>

<style>
.ltr {
  direction: ltr !important;
}

input[type="text"]::-webkit-outer-spin-button,
input[type="text"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="text"][type="number"] {
  -moz-appearance: textfield;
  appearance: textfield;
}
</style>

<style scoped>
.fixed {
  z-index: 1000;
}

.modal-content {
  z-index: 1001;
}
</style>